import { assertNever, type RefOrGetter } from '@/helpers'
import { type Ref, ref, toValue, watch } from 'vue'

export interface DragPincher {
    bound: Ref<boolean>

    drag: Ref<DragState>
    pinch: Ref<PinchState>

    currentAction: Ref<Action | null>
}

type ActionNone = { type: null }
type ActionDrag = {
    type: 'drag'
    touch: Touch
    touches: [Touch, ...Touch[]]
}
type ActionPinch = {
    type: 'pinch'
    touches: [Touch, Touch, ...Touch[]]
}
type Action = ActionNone | ActionDrag | ActionPinch
type ActionType = Action['type']

export type Coordinate = {
    x: number
    y: number
}

export function createEmptyCoordinate(): Coordinate {
    return {
        x: 0,
        y: 0,
    }
}

export function addCoordinate(c1: Coordinate, c2: Coordinate): Coordinate {
    return {
        x: c1.x + c2.x,
        y: c1.y + c2.y,
    }
}

export function subtractCoordinate(c1: Coordinate, c2: Coordinate): Coordinate {
    return {
        x: c1.x - c2.x,
        y: c1.y - c2.y,
    }
}

export function divideCoordinate(c: Coordinate, by: number): Coordinate {
    return {
        x: c.x / by,
        y: c.y / by,
    }
}

export type DragState = {
    start: Coordinate
    current: Coordinate
    delta: Coordinate
    deltaPrevious: Coordinate
}

export type DragCallbackEvent = DragState & {
    dragging: boolean
}

type PinchState = {
    start: PinchSnapshot
    current: PinchSnapshot
    delta: PinchSnapshot
    deltaPrevious: PinchSnapshot
}

type PinchCoordinates = {
    first: Coordinate
    second: Coordinate
}

type PinchSnapshot = {
    first: Coordinate
    second: Coordinate
    drag: Coordinate
    centre: Coordinate
    distance: number
    angle: number
}

type PinchEvent = PinchState & {
    pinching: boolean
}

function createEmptyPinch(): PinchSnapshot {
    return {
        first: createEmptyCoordinate(),
        second: createEmptyCoordinate(),
        drag: createEmptyCoordinate(),
        centre: createEmptyCoordinate(),
        distance: 0,
        angle: 0,
    }
}

function createPinchSnapshot(action: ActionPinch): PinchSnapshot {
    const [firstTouch, secondTouch] = action.touches

    const first: Coordinate = {
        x: firstTouch.clientX,
        y: firstTouch.clientY,
    }

    const second: Coordinate = {
        x: secondTouch.clientX,
        y: secondTouch.clientY,
    }

    const centre: Coordinate = {
        x: (first.x + second.x) / 2,
        y: (first.y + second.y) / 2,
    }

    const coordinates: PinchCoordinates = {
        first,
        second,
    }

    const { distance, angle } = calculateDistanceAndAngle(coordinates)

    return {
        first,
        second,
        drag: calculatePinchDrag(coordinates, coordinates),
        centre,
        distance,
        angle,
    }
}

function calculateDistanceAndAngle(coordinates: PinchCoordinates): {
    distance: number
    angle: number
} {
    const { first, second } = coordinates
    const diff = subtractCoordinate(second, first)

    const distance = Math.sqrt(Math.pow(diff.x, 2) + Math.pow(diff.y, 2))
    const angle = calculateAngleForDiff(diff)

    return {
        distance,
        angle,
    }
}

function calculateAngleForDiff(diff: Coordinate) {
    const { x, y } = diff
    // [-PI, PI]
    const theta = Math.atan2(y, x)
    // [-180, 180]
    const degrees = (theta * 180) / Math.PI

    if (degrees < 0) {
        // [0, 360]
        return degrees + 360
    }

    // [0, 360]
    return degrees
}

function createPinchDelta(
    current: PinchSnapshot,
    previous: PinchSnapshot
): PinchSnapshot {
    return {
        first: subtractCoordinate(current.first, previous.first),
        second: subtractCoordinate(current.second, previous.second),
        centre: subtractCoordinate(current.centre, previous.centre),
        drag: calculatePinchDrag(current, previous),
        distance: current.distance - previous.distance,
        angle: current.angle - previous.angle,
    }
}

function calculatePinchDrag(
    current: PinchCoordinates,
    previous: PinchCoordinates
): Coordinate {
    return divideCoordinate(
        subtractCoordinate(
            addCoordinate(current.first, current.second),
            addCoordinate(previous.first, previous.second)
        ),
        2
    )
}

export function useDragPincher(options: {
    container: RefOrGetter<HTMLElement | null>
    onTouchMove?: (e: TouchEvent) => void
    onDrag: (container: HTMLElement, drag: DragCallbackEvent) => void
    onPinch?: (container: HTMLElement, drag: PinchEvent) => void
}): DragPincher {
    const { container: containerRef, onDrag, onPinch } = options

    const bound = ref(false)

    const drag: Ref<DragState> = ref({
        current: createEmptyCoordinate(),
        start: createEmptyCoordinate(),
        delta: createEmptyCoordinate(),
        deltaPrevious: createEmptyCoordinate(),
    })

    const pinch: Ref<PinchState> = ref({
        current: createEmptyPinch(),
        start: createEmptyPinch(),
        delta: createEmptyPinch(),
        deltaPrevious: createEmptyPinch(),
    })

    const currentAction: Ref<Action | null> = ref(null)

    function handleDragCallback(dragging: boolean): void {
        const container = toValue(containerRef)

        if (!container) {
            return
        }

        onDrag(container, {
            ...drag.value,
            dragging,
        })
    }

    function handlePinchCallback(pinching: boolean): void {
        const container = toValue(containerRef)

        if (!container) {
            return
        }

        onPinch?.(container, {
            ...pinch.value,
            pinching,
        })
    }

    function handleActionStart(action: Action): void {
        const previousAction = currentAction.value

        currentAction.value = action

        if (previousAction) {
            handleActionEnd(previousAction.type)
        }

        switch (action.type) {
            case null:
                return
            case 'drag':
                return handleDragStart(action)
            case 'pinch':
                return handlePinchStart(action)
            default:
                return assertNever(action, undefined)
        }
    }

    function handleDragStart(action: ActionDrag): void {
        const coordinate: Coordinate = {
            x: action.touch.clientX,
            y: action.touch.clientY,
        }

        drag.value = {
            ...drag.value,
            start: coordinate,
            current: coordinate,
        }

        handleDragCallback(true)
    }

    function handlePinchStart(action: ActionPinch): void {
        const start = createPinchSnapshot(action)

        pinch.value = {
            ...pinch.value,
            start,
            current: start,
        }

        handlePinchCallback(true)
    }

    function isSameAction<T extends Action>(
        action: T,
        previousAction: T | null
    ): previousAction is T {
        return action.type === previousAction?.type
    }

    function handleAction(action: Action): void {
        const previousAction = currentAction.value

        if (!isSameAction(action, previousAction)) {
            return handleActionStart(action)
        }

        currentAction.value = action

        switch (action.type) {
            case null:
                return
            case 'drag':
                // @ts-expect-error
                return handleDrag(action, previousAction)
            case 'pinch':
                // @ts-expect-error
                return handlePinch(action, previousAction)
            default:
                return assertNever(action, undefined)
        }
    }

    function handleDrag(
        action: ActionDrag,
        previousAction: ActionDrag | null
    ): void {
        if (
            previousAction &&
            action.touches.length !== previousAction?.touches.length
        ) {
            handleDragEnd()
            handleDragStart(action)
        }

        const current: Coordinate = {
            x: action.touch.clientX,
            y: action.touch.clientY,
        }

        const previous = drag.value

        const delta = subtractCoordinate(current, previous.start)
        const deltaPrevious = subtractCoordinate(current, previous.current)

        drag.value = {
            current: current,
            start: previous.start,
            delta: delta,
            deltaPrevious: deltaPrevious,
        }

        handleDragCallback(true)
    }

    function handlePinch(
        action: ActionPinch,
        previousAction: ActionPinch
    ): void {
        if (
            previousAction &&
            action.touches.length !== previousAction.touches.length
        ) {
            handlePinchEnd()
            handlePinchStart(action)
        }

        const current = createPinchSnapshot(action)

        const previous = pinch.value

        const delta = createPinchDelta(current, previous.start)
        const deltaPrevious = createPinchDelta(current, previous.current)

        pinch.value = {
            current: current,
            start: previous.start,
            delta: delta,
            deltaPrevious: deltaPrevious,
        }

        handlePinchCallback(true)
    }

    function handleActionEnd(action: ActionType): void {
        if (!action) {
            return
        }

        switch (action) {
            case null:
                return
            case 'drag':
                return handleDragEnd()
            case 'pinch':
                return handlePinchEnd()
            default:
                return assertNever(action, undefined)
        }
    }

    function handleDragEnd(): void {
        handleDragCallback(false)

        drag.value = {
            current: createEmptyCoordinate(),
            start: createEmptyCoordinate(),
            delta: createEmptyCoordinate(),
            deltaPrevious: createEmptyCoordinate(),
        }
    }

    function handlePinchEnd(): void {
        handlePinchCallback(false)

        pinch.value = {
            current: createEmptyPinch(),
            start: createEmptyPinch(),
            delta: createEmptyPinch(),
            deltaPrevious: createEmptyPinch(),
        }
    }

    function onTouchStart(e: TouchEvent): void {
        handleAction(guessAction(e))
    }

    function onTouchMove(e: TouchEvent): void {
        if (options.onTouchMove) {
            options.onTouchMove(e)
        } else {
            e.preventDefault()
        }

        handleAction(guessAction(e))
    }

    function onTouchEnd(e: TouchEvent): void {
        handleAction(guessAction(e))
    }

    function guessAction(e: TouchEvent): Action {
        const touches = e.touches

        if (touches.length === 1 && touches[0]) {
            return {
                type: 'drag',
                touch: touches[0],
                touches: [touches[0]],
            }
        }

        if (onPinch && touches[0] && touches[1]) {
            const [, , ...rest] = touches

            return {
                type: 'pinch',
                touches: [touches[0], touches[1], ...rest],
            }
        }

        if (touches[0]) {
            const [, ...rest] = touches
            return {
                type: 'drag',
                touch: touches[0],
                touches: [touches[0], ...rest],
            }
        }

        return {
            type: null,
        }
    }

    watch(containerRef, (container, oldContainer) => {
        if (container) {
            container.addEventListener('touchstart', onTouchStart)
            container.addEventListener('touchmove', onTouchMove)
            container.addEventListener('touchend', onTouchEnd)
        }

        bound.value = !!container

        if (oldContainer) {
            oldContainer.removeEventListener('touchstart', onTouchStart)
            oldContainer.removeEventListener('touchmove', onTouchMove)
            oldContainer.removeEventListener('touchend', onTouchEnd)
        }
    })

    return {
        bound,

        drag,
        pinch,

        currentAction,
    }
}

if (import.meta.vitest) {
    const { describe, test, expect } = import.meta.vitest

    describe('calculatePinchDrag', () => {
        test('case 1', () => {
            const current: PinchCoordinates = {
                first: {
                    x: 100,
                    y: 100,
                },
                second: {
                    x: 200,
                    y: 100,
                },
            }

            const previous: PinchCoordinates = {
                first: {
                    x: 110,
                    y: 100,
                },
                second: {
                    x: 220,
                    y: 100,
                },
            }

            const coords = calculatePinchDrag(current, previous)
            expect(coords.x).toBe(-15)
        })

        test('case 2', () => {
            const current: PinchCoordinates = {
                first: {
                    x: 100,
                    y: 100,
                },
                second: {
                    x: 200,
                    y: 100,
                },
            }

            const previous: PinchCoordinates = {
                first: {
                    x: 90,
                    y: 100,
                },
                second: {
                    x: 240,
                    y: 100,
                },
            }

            const coords = calculatePinchDrag(current, previous)
            expect(coords.x).toBe(-15)
        })

        test('case 3', () => {
            const current: PinchCoordinates = {
                first: {
                    x: 30,
                    y: 100,
                },
                second: {
                    x: 50,
                    y: 100,
                },
            }

            const previous: PinchCoordinates = {
                first: {
                    x: -20,
                    y: 100,
                },
                second: {
                    x: 0,
                    y: 100,
                },
            }

            const coords = calculatePinchDrag(current, previous)
            expect(coords.x).toBe(50)
        })

        test('case 4', () => {
            const current: PinchCoordinates = {
                first: {
                    x: 190,
                    y: 100,
                },
                second: {
                    x: 180,
                    y: 100,
                },
            }

            const previous: PinchCoordinates = {
                first: {
                    x: 90,
                    y: 100,
                },
                second: {
                    x: 10,
                    y: 100,
                },
            }

            const coords = calculatePinchDrag(current, previous)
            expect(coords.x).toBe(135)
        })
    })

    describe('Test Coordinate functions', () => {
        test('addCoordinate', () => {
            expect(
                addCoordinate(
                    {
                        x: 23,
                        y: 221,
                    },
                    {
                        x: 100,
                        y: 100,
                    }
                )
            ).toStrictEqual({
                x: 123,
                y: 321,
            })
        })

        test('subtractCoordinate', () => {
            expect(
                subtractCoordinate(
                    {
                        x: 123,
                        y: 321,
                    },
                    {
                        x: 100,
                        y: 300,
                    }
                )
            ).toStrictEqual({
                x: 23,
                y: 21,
            })
        })

        test('divideCoordinate', () => {
            expect(
                divideCoordinate(
                    {
                        x: 222,
                        y: 444,
                    },
                    2
                )
            ).toStrictEqual({
                x: 111,
                y: 222,
            })
        })
    })
}
